import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  DEFAULT_LOCALE,
  detectLocale,
  hasKey,
  plural,
  translate,
  writeStoredLocale,
  type Locale,
  type PluralForms,
} from "@/i18n";

/**
 * Language.
 *
 * Held above authentication, because the login screen has to be readable before
 * anyone knows who is reading it. Once a user signs in, their stored `locale`
 * takes over — a teacher who set German on their phone should not have to set
 * it again here.
 */

interface I18nValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string, params?: Record<string, unknown>) => string;
  /** "1 teacher" / "5 teachers", chosen by `Intl.PluralRules`. */
  plural: (count: number, forms: PluralForms) => string;
}

/**
 * A key that is not in the catalogue.
 *
 * **In development this throws.** The alternative is a screen that renders
 * `billing.error.seatLimit` to a teacher, which is exactly the failure this
 * whole arrangement exists to prevent, and it is far cheaper to find while
 * writing the page than in a support email.
 *
 * In production it renders the key and logs. A thrown error there would take
 * out the surrounding page over a missing sentence, which is worse than an ugly
 * one — and the log is what tells us to fix it.
 */
function reportMissing(key: string): void {
  if (import.meta.env.DEV) {
    throw new Error(
      `[i18n] No such key: "${key}". Add it to src/i18n/en.ts and src/i18n/de.ts.`
    );
  }
  // eslint-disable-next-line no-console
  console.error(`[i18n] No such key: "${key}"`);
}

const I18nContext = createContext<I18nValue | undefined>(undefined);

export function I18nProvider({
  children,
  initialLocale,
}: {
  children: ReactNode;
  /** Tests pass this; the app detects. */
  initialLocale?: Locale;
}) {
  const [locale, setLocaleState] = useState<Locale>(initialLocale ?? DEFAULT_LOCALE);

  // Detection happens in an effect rather than in `useState`'s initialiser so
  // that a server-side or test render without `window` still works.
  useEffect(() => {
    if (initialLocale) return;
    setLocaleState(detectLocale());
  }, [initialLocale]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    writeStoredLocale(next);
  }, []);

  const value = useMemo<I18nValue>(
    () => ({
      locale,
      setLocale,
      t: (key, params) => {
        if (!hasKey(key)) reportMissing(key);
        return translate(locale, key, params ?? {});
      },
      plural: (count, forms) => plural(locale, count, forms),
    }),
    [locale, setLocale]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nValue {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}

/** The common case, so components read `const t = useT()`. */
export function useT(): I18nValue["t"] {
  return useI18n().t;
}
